// Coach Scorecard — the gate-selection policy (a pure leaf, no engine imports).
// Separates *measurement* from *gating*: every dimension is measured every bar, but a
// bar's mastery is decided only by the ACTIVE subset — the proficiency baseline ∪ the
// current lesson's focus. See docs/architecture.md → "The Coach Scorecard".

/** A measured mastery dimension. */
export type Dimension = "completeness" | "correctness" | "timing" | "dynamics" | "articulation";

// The existing three gates are always active — a conservative rollout (scorecard §8):
// the scorecard adds expression gating without ever relaxing today's behaviour.
const BASE: readonly Dimension[] = ["completeness", "correctness", "timing"];

/**
 * The dimension a lesson focuses, from its `teaches`/`uses` tags — always gated while
 * that lesson is practised. `null` for plain technique/reading repertoire, which the
 * base gates already cover (reading = the `correctness` gate with the crutch faded).
 */
export function focusDimension(skillIds: readonly string[]): Dimension | null {
  for (const id of skillIds) {
    if (id.startsWith("dynamics-")) return "dynamics";
    if (id.startsWith("articulation-")) return "articulation";
    if (id.startsWith("rhythm-")) return "timing";
  }
  return null;
}

/**
 * Active mastery gates = proficiency baseline ∪ lesson focus. Expression dimensions
 * switch on as the learner advances (articulation ~grade 3, dynamics ~grade 4) or when
 * a lesson focuses them. Thresholds are tunable; this picks *which* gates are live.
 */
export function activeGates(proficiency: number, focus: Dimension | null): Set<Dimension> {
  const active = new Set<Dimension>(BASE);
  if (proficiency >= 2.5) active.add("articulation");
  if (proficiency >= 3.5) active.add("dynamics");
  if (focus) active.add(focus);
  return active;
}

// Proficiency with nothing mastered yet — mirrors curriculum-state.ts FLOOR. The ramp
// is anchored here so a raw beginner sits exactly at the floor pass-rate.
const PROF_FLOOR = 1.0;

/**
 * Effective on-time pass-rate (%) for the timing gate, ramped with global proficiency:
 * the floor pass-rate at PROF_FLOOR, climbing linearly to the ceiling by `fullProf`.
 * The ±timing-ms window is untouched — only the SHARE of notes that must land inside it
 * grows with skill. `floorPct === ceilingPct` ⇒ today's flat rule.
 */
export function rampedTimingPct(
  proficiency: number,
  floorPct: number,
  fullProf: number,
  ceilingPct: number,
): number {
  if (fullProf <= PROF_FLOOR) return ceilingPct;
  const t = Math.max(0, Math.min(1, (proficiency - PROF_FLOOR) / (fullProf - PROF_FLOOR)));
  return floorPct + t * (ceilingPct - floorPct);
}
